import { supabase } from "../../../lib/supabase";
import { sanitizeFileName } from "../lib/utils/utils-functions";
import { DBOperationResult } from "../types/types";
import { useCallback } from "react";
import { Logger } from "../../../lib/logger/Logger";
import { StorageBucketsEnum } from "../types/enums";
import {
	FileEntityType,
	FileWithShare,
	SharesWithFileInfo,
} from "../../../lib/supabase/supabaseTypes";
import { FileOptions } from "@supabase/storage-js/dist/main/lib/types";
import { showNotification } from "../store/Central/selectors";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { isConfigTrue } from "../pages/organization/subpages/administration/konfigurationseinstellungen.page";

const BUCKET_NAME = StorageBucketsEnum.V1;

const MIME_EXTENSIONS: Record<string, string> = {
	"application/pdf": ".pdf",
	"image/png": ".png",
	"image/jpeg": ".jpg",
	"text/plain": ".txt",
	"application/json": ".json",
};

/** Generated documents are stored without an extension; derive it from the blob's mime type. */
export const withMimeExtension = (fileName: string, mime: string): string => {
	const lastDotIndex = fileName.lastIndexOf(".");
	const extractedExtension =
		lastDotIndex !== -1 ? fileName.substring(lastDotIndex + 1) : null;
	// Validate extension: should be short (max 10 chars) and alphanumeric (typical file extensions)
	const isValidExtension =
		extractedExtension &&
		extractedExtension.length <= 10 &&
		/^[a-zA-Z0-9]+$/.test(extractedExtension);
	const extension = isValidExtension
		? "." + extractedExtension
		: MIME_EXTENSIONS[mime];
	// Construct proper filename: if extension is invalid, decide whether to replace or append
	// If the part before the dot is very short (1-2 chars), treat entire filename as base name
	// Otherwise, replace the invalid extension with the valid one
	if (isValidExtension || !extension) return fileName;
	return lastDotIndex !== -1 && lastDotIndex > 2
		? fileName.substring(0, lastDotIndex) + extension
		: fileName + extension;
};

/**
 * From Supabase package
 */
type FileBody =
	| ArrayBuffer
	| ArrayBufferView
	| Blob
	| Buffer
	| File
	| FormData
	| NodeJS.ReadableStream
	| ReadableStream<Uint8Array>
	| URLSearchParams
	| string;

interface UploadFileWithProvidedPathProps {
	filePath: string;
	fileBody: FileBody;
	fileOptions?: FileOptions;
	bucketName?: string;
}

/**
 * Extends entity in supabase "files" table with url property retrieved via .createSignedUrl()
 */
export type FileEntityTypeWithUrl = FileEntityType & { url: string };

export type FileWithShareWithUrl = FileWithShare & { url: string };

export type SharesWithFileInfoWithUrl = SharesWithFileInfo & { url: string };

const bareUpload = async (
	path: string,
	file: FileBody,
	fileOptions?: FileOptions,
	bucketName: string = BUCKET_NAME
) => {
	Logger.info(`sup ${bucketName}/${path}`);
	// TODO: sanitizing the path here is problematic,
	// because it doesn't guarantee the path in the files table is identically sanitized
	const sanitizedPath = sanitizeFileName(path);
	const { data, error } = await supabase.storage
		.from(bucketName)
		.upload(sanitizedPath, file, fileOptions);
	if (data && !error) {
		return { success: true, pathName: sanitizedPath };
	} else {
		Logger.error(error, {}, "Error uploading file");
		return { success: false, pathName: null };
	}
};

export const uploadFileWithProvidedPath = async ({
	filePath,
	fileBody,
	fileOptions,
	bucketName,
}: UploadFileWithProvidedPathProps): Promise<{
	success: boolean;
	pathName: string | null;
}> => {
	return bareUpload(filePath, fileBody, fileOptions, bucketName);
};

export const uploadFileWithPublicUrl = async (
	filePath: string,
	fileBody: FileBody,
	fileOptions?: FileOptions
) => {
	const { success, pathName } = await bareUpload(
		filePath,
		fileBody,
		fileOptions,
		StorageBucketsEnum.PUBLIC_IMAGES
	);
	if (!success || !pathName) {
		return { success: false, pathName: null, url: null };
	}
	const { data } = supabase.storage
		.from(StorageBucketsEnum.PUBLIC_IMAGES)
		.getPublicUrl(pathName);
	if (data?.publicUrl) {
		return { success: true, pathName, url: data.publicUrl };
	} else {
		return { success: false, pathName: null, url: null };
	}
};

/**
 * useStorage - Custom hook to upload and fetch files from Supabase Storage
 * @returns uploadFiles, fetchFiles, deleteFiles, fetchJobFiles, fetchClientFiles
 * @example const { uploadFile, fetchFiles, deleteFiles } = useStorage();
 */
export const useStorage = () => {
	// We take the organization from the realtime store since this component is shared between
	// dL and dC and both of them add the org to the realtime store when initialized
	// instead of direct central store access
	const organization = useRealtimeStore((state) => state.organization);

	/**
	 * fileUrl - Retrieve a signed url for file for 1 hour
	 * @param fileName - Name of the file as saved in the supabase storage bucket
	 * @param quality - For images only, quality of the image less means faster fetch
	 * @example const {  data, error } = await fileUrl( "public/avatar_pic.png");
	 */
	const fileUrl = useCallback(
		async (fileName: string, quality: number = 80) => {
			try {
				const { data, error } = await supabase.storage
					.from(BUCKET_NAME)
					.createSignedUrl(fileName, 3600, {
						transform: {
							quality,
						},
					});

				return { data, error };
			} catch (err: unknown) {
				return { data: null, error: err };
			}
		},
		[BUCKET_NAME]
	);

	/**
	 * @returns
	 * { success: boolean, data: FileEntityTypeWithUrl | null, error: string | null}
	 */
	const fetchFile = useCallback(
		async (
			file: FileWithShare | FileEntityType | SharesWithFileInfoWithUrl
		): Promise<
			DBOperationResult<
				| FileWithShareWithUrl
				| FileEntityTypeWithUrl
				| SharesWithFileInfoWithUrl
			>
		> => {
			if (!file.path_name) {
				Logger.error("No file to fetch");
				return {
					success: false,
					error: "No file to fetch",
					data: null,
					status: 404,
				};
			}
			Logger.info(`fetchFile 2 ${BUCKET_NAME}/${file.path_name}`);
			const { data, error } = await supabase.storage
				.from(BUCKET_NAME)
				.createSignedUrl(file.path_name, 3600);
			if (data?.signedUrl) {
				const newFile = {
					...file,
					url: data.signedUrl,
				};
				return { success: true, data: newFile, error, status: 200 };
			}
			Logger.error(error, {}, "Error fetching file");
			// TODO: check if this is the correct status code or if it should be taken from await supabase.storage
			return {
				success: false,
				error: "Error fetching file",
				data: null,
				status: 500,
			};
		},
		[]
	);

	/**
	 * @returns
	 * { success: boolean, data: FileEntityTypeWithUrl | null, error: string | null}
	 */
	const fetchFileFromPath = useCallback(
		async (filePathName: string): Promise<DBOperationResult<string>> => {
			const { data, error } = await supabase.storage
				.from(BUCKET_NAME)
				.createSignedUrl(filePathName, 3600);
			if (data?.signedUrl) {
				return {
					success: true,
					data: data.signedUrl,
					error,
					status: 200,
				};
			}
			Logger.error(error, {}, "Error fetching file");
			// TODO: check if this is the correct status code or if it should be taken from await supabase.storage
			return {
				success: false,
				error: "Error fetching file",
				data: null,
				status: 500,
			};
		},
		[]
	);

	/**
	 * @returns
	 * { success: boolean, data: FileEntityTypeWithUrl[] | null, error: string | null}
	 */
	const fetchFiles = useCallback(
		async (
			files: FileWithShare[]
		): Promise<DBOperationResult<FileWithShareWithUrl[]>> => {
			const results = await Promise.all(
				files.map((file) => fetchFile(file))
			);
			const validResults = results.filter(
				(result) => result.data && result.data.url
			);
			// check if number of results is the same as number of valid files
			if (results.length !== validResults.length) {
				Logger.error(
					"useStorage: Fetched files without valid url",
					{},
					results
				);
			}
			return {
				success: true,
				// because the results are filtered, we can safely cast the data as FileEntityTypeWithUrl
				data: validResults.map(
					(result) => result.data as FileWithShareWithUrl
				),
				error: null,
				status: 200,
			};
		},
		[]
	);

	/**
	 * deleteFiles - Deletes one or multiple files from a Supabase Storage bucket
	 * @param paths: string[] - Paths of the files to delete
	 * @returns OperationResult
	 * @example const { success, data, error } = await deleteFile(["avatars/public/avatar.png"]);
	 */
	const deleteFiles = useCallback(async (paths: string[]) => {
		Logger.log("Deleting files:", paths);
		const { data, error } = await supabase.storage
			.from(BUCKET_NAME)
			.remove(paths);
		return {
			success: !error,
			data: data ?? null,
			error: error?.message ?? null,
			status: error ? 500 : 200,
		};
	}, []);

	const openFileInNewTab = async (filePathName: string) => {
		const {
			success,
			data: signedUrl,
			error,
		} = await fetchFileFromPath(filePathName);
		if (success && signedUrl) {
			window.open(signedUrl, "_blank")?.focus();
		} else {
			showNotification({
				message: "Fehler beim Öffnen der Datei",
				type: "error",
			});
			Logger.log("Error opening file", error);
		}
	};

	/**
	 * Downloads a blob directly (for in-browser created files like PDFs, CSVs)
	 * Uses organization preferences to determine download location
	 * @param blob - The blob to download
	 * @param fileName - The filename for the download
	 */
	const downloadBlob = async (
		blob: Blob,
		fileName: string,
		overwriteCustomPath?: boolean
	) => {
		const customPath = isConfigTrue(organization?.config, "custom_path");
		const properFileName = withMimeExtension(fileName, blob.type);
		const extensionIndex = properFileName.lastIndexOf(".");
		try {
			if (
				!("showSaveFilePicker" in window) ||
				!customPath ||
				overwriteCustomPath
			) {
				const downloadUrl = URL.createObjectURL(blob);

				const link = document.createElement("a");
				link.href = downloadUrl;
				link.download = properFileName || "Unbenannte_Datei";

				// Add the element to the DOM first for better browser compatibility
				document.body.appendChild(link);
				link.click();

				document.body.removeChild(link);

				// Frees the memory allocated for the downloadUrl
				URL.revokeObjectURL(downloadUrl);
			} else {
				const picker = await (window as any).showSaveFilePicker({
					suggestedName: properFileName,
					types: [
						{
							description: "File",
							accept: {
								[blob.type]:
									extensionIndex === -1
										? []
										: [
												properFileName.slice(
													extensionIndex
												),
											],
							},
						},
					],
				});

				const writable = await picker.createWritable();
				await writable.write(blob);
				await writable.close();
			}
		} catch (err) {
			if (
				!(
					!!err &&
					typeof err === "object" &&
					(err as DOMException).name === "AbortError"
				)
			) {
				showNotification({
					message:
						"Beim Herunterladen der Datei ist ein Fehler aufgetreten",
					type: "error",
				});
				Logger.log("Error in downloadBlob function", err);
			}
		}
	};

	/**
	 * Downloads a file from Supabase storage
	 * @param filePathName - Path to the file in Supabase storage
	 */
	const downloadFromSupabase = async (filePathName: string) => {
		try {
			const {
				success,
				data: signedUrl,
				error,
			} = await fetchFileFromPath(filePathName);

			if (success && signedUrl) {
				const response = await fetch(signedUrl);
				if (!response.ok)
					throw new Error("Failed to fetch file for download");

				const blob = await response.blob();
				const fileName =
					filePathName.split("/").pop() || "Unbenannte_Datei";

				await downloadBlob(blob, fileName);
			} else {
				showNotification({
					message: "Fehler beim Herunterladen der Datei",
					type: "error",
				});
				Logger.log("Error downloading file from Supabase", error);
			}
		} catch (err) {
			showNotification({
				message:
					"Beim Herunterladen der Datei ist ein Fehler aufgetreten",
				type: "error",
			});
			Logger.log("Error in downloadFromSupabase function", err);
		}
	};

	return {
		fetchFile,
		fetchFiles,
		deleteFiles,
		fileUrl,
		fetchFileFromPath,
		openFileInNewTab,
		downloadBlob,
		downloadFromSupabase,
	};
};
